<template>
  <div
    v-if="caseTypesWithPrices.length > 1"
    id="case-type-group-selector"
    class="case-type-group-selector"
  >
    <div class="case-color__section case-type-color-title">
      {{ $t('product_form.choose_case_color') }}:<em class="color-select">
        {{
          (selectedOption &&
            (selectedOption.name || selectedOption.group_name)) ||
          "'-'"
        }}</em
      >
    </div>

    <ul class="case-type-group-selector__list">
      <li
        v-for="caseType in caseTypesWithPrices"
        :key="caseType.id"
        :class="[
          'case-type-group-selector__list-item',
          {
            active: getCaseTypeValue(caseType) === selectedCaseTypeId,
            'out-of-stock': !hasStock(caseType),
          },
        ]"
        :data-id="caseType.id"
        @click="selectCaseType(caseType)"
      >
        <div class="group-image__container">
          <span v-if="isPreSale" class="out-of-stock__text">{{
            $t('product_form.coming_soon')
          }}</span>
          <span v-else-if="!hasStock(caseType)" class="out-of-stock__text">{{
            $t('product_form.sold_out')
          }}</span>
          <img
            class="group-image__img"
            :src="
              convertImagekit(caseType.group_image || caseType.case_image, 100)
            "
            :alt="caseType.name"
          />
        </div>
        <em class="group-image__name">{{ caseType.name }}</em>
      </li>
    </ul>
  </div>
</template>

<script>
/* eslint-disable camelcase */

import convertImagekit from '@/utils/convertImagekit'
import caseTypeSelectable from '~/mixins/caseTypeSelectable'

export default {
  name: 'CaseTypeGroupSelector',
  mixins: [caseTypeSelectable],
  props: {
    deviceId: {
      type: Number,
      default: null,
    },
    productId: {
      type: Number,
      default: null,
    },
    variantId: {
      type: Number,
      default: null,
    },
    caseTypes: {
      type: Array,
      default: null,
    },
    caseTypeDefaultId: {
      type: [Number, String],
      default: null,
    },
    isCrossell: {
      type: Boolean,
      default: false,
    },
    productStock: {
      type: Object,
      required: true,
    },
    isPreSale: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedCaseTypeId: null,
      caseTypesWithPrices: [],
    }
  },

  computed: {
    selectedOption() {
      return this.caseTypesWithPrices?.find(
        (opt) => this.getCaseTypeValue(opt) === this.selectedCaseTypeId
      )
    },
    isSlimAirOnIphone13() {
      return [777].includes(this.deviceId)
    },
    isDeluxeRule() {
      return false // this.$isGlobalStore // && [505, 506, 507, 133].includes(this.deviceId)
    },
    isEcoRule() {
      return false // this.$isGlobalStore && this.$route.path.match('iphone-12-pro-max')
    },
    isSoftFlexible() {
      return false // this.$isGlobalStore
    },
  },

  watch: {
    async caseTypes(caseTypes) {
      await this.fetchCurrentPrices()
    },
    selectedCaseTypeId(ctId) {
      this.$emit('updateCaseTypeId', ctId)
    },
  },

  async mounted() {
    if (this.caseTypes.length) await this.fetchCurrentPrices()
  },

  methods: {
    convertImagekit,
    setCaseTypePrice(caseType) {
      if (this.isCrossell) return
      this.$store.dispatch('product/setCurrentPrice', caseType?.price)
      this.$store.dispatch('product/setOldPrice', caseType?.old_price)
    },
    hasStock(opt) {
      return (
        // eslint-disable-next-line camelcase
        opt.devices.find((dev) => dev.device_id === this.deviceId)?.in_stock
      )
    },
    getCaseTypeValue(caseType) {
      return caseType?.id
    },
    selectCaseType(caseType) {
      this.selectedCaseTypeId = caseType?.id
      this.setCaseTypePrice(caseType)
    },

    getOptionDevice(option) {
      return option.devices.find((dv) => dv.device_id === this.deviceId)
    },
    async fetchCurrentPrices() {
      try {
        const options = this.caseTypes

        let optionsPrices = []
        const caseDeviceIds = options.map(
          (co) => this.getOptionDevice(co)?.case_device_id
        )

        if (
          options.length &&
          caseDeviceIds.length &&
          caseDeviceIds.filter((cd) => !!cd).length
        ) {
          const newOptionsPrices = await this.$axios.$get('/products/price', {
            params: {
              ...this.$store.getters.storeInfo,
              variant_id: this.variantId,
              case_device_ids: caseDeviceIds.join(','),
              product_id: this.productId,
            },
          })

          options.map((co) => {
            const caseDevice = this.getOptionDevice(co)

            const {
              price,
              old_price,
              promotion,
              case_device_id,
            } = newOptionsPrices.find(
              (nop) => nop.case_device_id === caseDevice.case_device_id
            )

            optionsPrices.push({
              ...co,
              case_device_id,
              price,
              old_price,
              promotion,
            })
          })
        }

        let rest = optionsPrices
          .filter(
            (op) =>
              !op.name?.match('Standard') &&
              !(op.name?.match('Impacto Slim') || op.name?.match('Impact Slim'))
          )
          ?.sort((a, b) => a.id - b.id)
          ?.sort((a, b) => Number(a.price) - Number(b.price))

        const sortByCaseTypeName = (caseTypeName) => {
          rest = rest.filter((op) => !op.name?.match(caseTypeName))
          return this.sortByIdAndPrices(
            optionsPrices.filter((op) => op.name?.match(caseTypeName))
          )
        }

        let deluxes = []
        let slimair = []

        if (this.isDeluxeRule) {
          deluxes = sortByCaseTypeName('Deluxe')
        }

        if (this.isSlimAirOnIphone13) {
          slimair = sortByCaseTypeName('Slim Air')
        }

        let ecos = []
        if (this.isEcoRule) {
          ecos = sortByCaseTypeName('ECO')
        }

        let softs = []
        if (this.isSoftFlexible) {
          softs = sortByCaseTypeName('Soft')
        }

        const standards = this.sortByIdAndPrices(
          optionsPrices.filter((op) => op.name?.match('Standard'))
        )

        const impactslims = this.sortByIdAndPrices(
          optionsPrices.filter(
            (op) =>
              op.name?.match('Impacto Slim') || op.name?.match('Impact Slim')
          )
        )

        optionsPrices = [
          ...slimair,
          ...softs,
          ...ecos,
          ...deluxes,
          ...standards,
          ...impactslims,
          ...rest,
        ].filter((v, i, a) => a.indexOf(v) === i)

        const withStock = optionsPrices.filter((op) => this.isInStock(op))
        const withoutStock = optionsPrices.filter((op) => !this.isInStock(op))
        optionsPrices = [withStock, withoutStock].flat()

        this.caseTypesWithPrices = optionsPrices

        const defaultCaseType =
          this.caseTypesWithPrices.find(
            (ct) => ct.id === Number(this.caseTypeDefaultId)
          ) || this.caseTypesWithPrices[0]

        if (defaultCaseType) {
          this.selectedCaseTypeId = defaultCaseType.id
          this.setCaseTypePrice(defaultCaseType)
        }
      } catch (err) {
        this.$displayTopMessage(this.$t('action_error'))
        this.$sentry.captureException(err.response || err)
      }
    },
    sortByIdAndPrices(options) {
      return options
        ?.sort((a, b) => a.id - b.id)
        ?.sort((a, b) => Number(a.price) - Number(b.price))
    },
  },
}
</script>

<style lang="scss" scoped>
.case-type-group-selector {
  margin-top: 25px;
}

.case-type-group-selector__list {
  margin-top: 11px;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  grid-column-gap: 10px;
  grid-row-gap: 10px;
  list-style-type: none;

  @media (min-width: $v4_desktop) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
  }
}

.case-type-group-selector__list-item {
  position: relative;

  &.out-of-stock {
    filter: grayscale(80%);
  }

  .out-of-stock__text {
    z-index: 2;
    position: absolute;
    left: 4px;
    right: 4px;
    bottom: -8px;
    color: #fff;
    background-color: #c2c2c6;
    border-radius: 3px;
    padding: 3px 2px 4px;
    font-size: 9px;
    font-weight: 700;
    text-align: center;
    line-height: 10px;
  }

  &.active {
    box-shadow: 0px 0px 5px 2px rgba(0, 0, 0, 0.1);

    .group-image__container,
    .group-image__container:hover {
      border-color: $v4_color_primary;
    }

    .group-image__container {
      box-shadow: 0 0 0 0.5px $v4_color_primary;
    }
  }
}

.group-image__container,
.group-image__img {
  border-radius: 3px;
}

.group-image__container {
  display: block;
  position: relative;
  border: 0.5px solid $v4_color_line_lighter;
  box-shadow: 0 0 0 0.5px white;
  cursor: pointer;
  transition: border-color 150ms ease-in-out;
  padding-top: 100%;

  &:hover {
    border-color: #bbb;
  }
}

.group-image__img {
  display: block;
  width: 100%;
  height: auto;
  position: absolute;
  top: 0;
}

.group-image__name {
  display: none;
}

.case-type-color-title .color-select {
  font-weight: bold;
  color: $v4_color_primary;
  margin-left: 3px;
  text-transform: none;
  font-size: 13px;
  color: $v4_color_text;
}

.subsection-title {
  position: relative;
  text-transform: uppercase;
  font-weight: 400;
  color: #999;
  font-size: 11px;
  height: 14px;
  margin-bottom: 7px;

  &.subsection-title--top-margin {
    margin-top: 15px;
  }
}

.case-type-color-title {
  margin-top: 13px;
  text-transform: uppercase;
  font-weight: normal;
  color: #999;
  font-size: 11px;
}
</style>
